import type { LocationState } from "@trip/contracts/bridge";
import { MapPin, MapPinOff } from "lucide-react";
import { useEffect, useState } from "react";
import { nativeBridge, type Bridge } from "../lib/bridge.ts";
import { ICON } from "../ui/index.ts";

/**
 * Always visible in the app (owner decision: the person must always know whether they are being
 * tracked): "Location sharing is on" with a one-tap way to stop, or a plain "Location off". Nothing
 * in a normal browser. Without "Allow all the time" the route stops when the app is closed, so the
 * indicator says so and offers the fix (owner decision: the route places media on the map).
 */
export function LocationIndicator({ bridge = nativeBridge() }: { bridge?: Bridge | null }) {
  const [state, setState] = useState<LocationState | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!bridge) return;
    void bridge
      .call("location.state", {})
      .then(setState)
      .catch(() => undefined);
    const off = bridge.on("location.changed", setState);
    return () => void off();
  }, [bridge]);

  if (!bridge || !state) return null;
  if (!state.active) {
    return (
      <span className="location-on">
        <span className="pill">
          <MapPinOff {...ICON} aria-hidden="true" />
          Location off
        </span>
      </span>
    );
  }
  const limited = state.background === false;
  return (
    <span className="location-on">
      <button
        type="button"
        className={`pill ${limited ? "wait" : "good"}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MapPin {...ICON} aria-hidden="true" />
        {limited ? "Location only while the app is open" : "Location sharing is on"}
      </button>
      {open && (
        <span className="location-menu">
          {limited && (
            <>
              <span className="small">
                Your route stops being recorded when the app is closed, so recordings taken then
                can&apos;t be placed on the map. Choose &ldquo;Allow all the time&rdquo; for this
                app.
              </span>
              <button
                type="button"
                className="button small primary"
                onClick={() => {
                  setOpen(false);
                  void bridge
                    .call("location.allowAlways", {})
                    .then(setState)
                    .catch(() => undefined);
                }}
              >
                Allow all the time
              </button>
            </>
          )}
          <span className="small">Everyone on this trip can see your route.</span>
          <button
            type="button"
            className="button small"
            onClick={() => {
              setOpen(false);
              void bridge.call("location.stop", {}).catch(() => undefined);
            }}
          >
            Stop sharing location
          </button>
        </span>
      )}
    </span>
  );
}
